/* ═════════════════════════════════════════════════
   Sivun avautuessa haettavan listan lataustila (Toni 15.10.2026 klo 08.35, kohta 2).

   Ennen tätä 15 sivua haki listan (kohteet ym.) ja nieli virheen `.catch(() => {})`:llä. Kun verkko katkesi, valitsin näytti
   pelkän «Valitse kohde…» -tekstin, ja käyttäjä luuli, ettei hänellä ole kohteita (pekilo 15.10. klo 07.50). Nyt:
   latauksen aikana «Ladataan…», virheessä «<lista> haku epäonnistui» + «Yritä uudelleen», ja tyhjä lista vain kun haku
   onnistui ja palautti nollan. Taustakyselyt (laskurit, lukemattomat viestit, hakuehdotukset) jäävät hiljaisiksi —
   virheilmoitus 30 sekunnin välein olisi pahempi kuin hiljaisuus.
   ═════════════════════════════════════════════════ */
import { useCallback, useState } from "react";
import { ApiError } from "./api";
import { t, virheTeksti, type KaannosAvain } from "./kieli";

export type Lataustila = "ladataan" | "virhe" | "valmis";

export interface Lataus {
  tila: Lataustila;
  virhe: string | null;
  /** Kasvaa «Yritä uudelleen» -painalluksesta; lataava efekti ottaa sen riippuvuudekseen. */
  kerta: number;
  uudelleen: () => void;
  /** Seuraa lupausta: onnistuminen → valmis, hylkäys → virhe (ei koskaan tyhjää hiljaisuutta). */
  seuraa: <T>(p: Promise<T>) => void;
}

export function useLataus(): Lataus {
  const [tila, setTila] = useState<Lataustila>("ladataan");
  const [virhe, setVirhe] = useState<string | null>(null);
  const [kerta, setKerta] = useState(0);
  const seuraa = useCallback(<T,>(p: Promise<T>) => {
    setTila("ladataan"); setVirhe(null);
    p.then(() => setTila("valmis"),
      (e: unknown) => {
        setTila("virhe");
        setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : e instanceof Error ? e.message : String(e));
      });
  }, []);
  const uudelleen = useCallback(() => setKerta((k) => k + 1), []);
  return { tila, virhe, kerta, uudelleen, seuraa };
}

/** Näyttää latauksen tai virheen; valmiina ei mitään. `nimi` = käännösavain, esim. «lataus.kohteet». */
export function LatausTila({ l, nimi }: { l: Lataus; nimi: KaannosAvain }) {
  if (l.tila === "valmis") return null;
  if (l.tila === "ladataan") return <p className="dim" data-listalataus="ladataan">{t("lataus.ladataan")}</p>;
  return (
    <p className="huom huom--vika" role="alert" data-listalataus="virhe">
      {t(nimi)}{l.virhe ? <span className="dim"> ({l.virhe})</span> : null}{" "}
      <button type="button" className="nappi nappi--kevyt nappi--pieni" data-listalataus-uudelleen="" onClick={l.uudelleen}>
        {t("lataus.yrita")}
      </button>
    </p>
  );
}
